// @ts-nocheck
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  fetchAssignmentById,
  updateAssignmentGrades,
  duplicateAssignment,
  publishAssignmentGradesToDataPoints,
  calculateGradingStats,
} from '../../../services/assignments';
import './Assignments.css';
import TaskThreadsPanel from '../../../components/LearningTasks/TaskThreadsPanel';
import { SCORING_METHODS } from '../../../services/assignments';
import { SUBMISSION_STATUSES } from '../../../utils/successSkillsSubmission';
import { submissionStatusFor, timelinessComponents, timelinessStandard } from '../../../utils/taskTimeliness';

const MASTERY_CHOICES = [
  { score: 1, color: 'red', colorLabel: 'Red', label: 'Not Yet' },
  { score: 2, color: 'yellow', colorLabel: 'Yellow', label: 'Learning Zone' },
  { score: 3, color: 'lightgreen', colorLabel: 'Light Green', label: 'On Track' },
  { score: 4, color: 'green', colorLabel: 'Green', label: 'Proficient' },
];

export default function AssignmentGradingGrid() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const teacherId = user?.user_id || user?.id || null;

  const [assignment, setAssignment] = useState<any>(null);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');

  // Editable Grid State
  // { [studentId]: { [mlsCode]: { score, color, notes, isMissing, notAssessed } } }
  const [grades, setGrades] = useState<any>({});
  const [studentNotes, setStudentNotes] = useState<any>({});
  const [submissionStatuses, setSubmissionStatuses] = useState<any>({});
  const timingComponents = timelinessComponents(assignment || {});
  const changeSubmissionStatus = (studentId, component, status) => {
    setSubmissionStatuses((previous) => ({
      ...previous,
      [studentId]: { ...previous[studentId], [component]: status },
    }));
    setSaveStatus('unsaved');
  };
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved' | 'unsaved' | 'saving'
  const [syncStatus, setSyncStatus] = useState('idle'); // 'idle' | 'syncing' | 'synced' | 'error'
  const [syncMessage, setSyncMessage] = useState<any>('');

  // Focus & Navigation State
  const [activeCell, setActiveCell] = useState<any>(null); // { studentId, mlsCode }

  // Note Modal / Popover State
  const [noteModal, setNoteModal] = useState<any>(null); // { studentId, studentName, mlsCode, note }

  // Filters
  const [studentFilter, setStudentFilter] = useState<any>('');
  const [completionFilter, setCompletionFilter] = useState('all'); // 'all' | 'ungraded' | 'needs_support'

  // View Mode: 'grid' (Interactive Spreadsheet) | 'details' (Overview & Student Performance)
  const [activeTab, setActiveTab] = useState('grid');

  const handleOpenEditAssignment = async () => {
    if (!assignment) return;
    try {
      if (saveStatus === 'unsaved') {
        setSaveStatus('saving');
        await updateAssignmentGrades(assignment.id, grades, studentNotes, assignment.status, teacherId, submissionStatuses);
        setSaveStatus('saved');
      }
      navigate(`/teacher/assignments/${assignment.id}/edit`);
    } catch (err: any) {
      setSaveStatus('unsaved');
      setSyncStatus('error');
      setSyncMessage(`Could not save scores before editing task: ${err.message}`);
    }
  };

  const handleDuplicate = async () => {
    if (!window.confirm(`Duplicate assignment "${assignment.title}"?`)) return;
    try {
      const dup = await duplicateAssignment(assignment.id, teacherId);
      navigate(`/teacher/assignments/${dup.id}`);
    } catch (err: any) {
      alert(`Could not duplicate assignment: ${err.message}`);
    }
  };

  const handleExportCSV = () => {
    if (!assignment) return;
    const stds = assignment.standards || [];
    const stdHeaders = stds.map((s) => `"${s.mlsCode} (${s.friendlyTitle || s.mlsCode})"`);
    const csvRows = [
      ['"Student ID"', '"Student Name"', '"Class"', '"Assignment Status"', ...stdHeaders,
        ...timingComponents.map((component) => `"Submission: ${component.label}"`), '"Teacher Feedback"'].join(','),
    ];

    (assignment.students || []).forEach((stu) => {
      const stuGrades = grades[stu.studentId] || {};
      const stdScores = stds.map((s) => {
        const cell = stuGrades[s.mlsCode];
        if (!cell) return '""';
        if (cell.isMissing) return '"Missing"';
        if (cell.notAssessed) return '"NA"';
        return `"${cell.score || ''}"`;
      });
      const note = studentNotes[stu.studentId] || '';
      csvRows.push([
        `"${stu.studentId}"`,
        `"${stu.name}"`,
        `"${assignment.className}"`,
        `"${assignment.status}"`,
        ...stdScores,
        ...timingComponents.map((component) => `"${submissionStatusFor({ ...assignment, submissionStatuses }, stu.studentId, component.key).label}"`),
        `"${note.replace(/"/g, '""')}"`,
      ].join(','));
    });

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${(assignment.title || 'Assignment').replace(/[^a-zA-Z0-9_-]/g, '_')}_Scores.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError('');
        const data = await fetchAssignmentById(assignmentId, teacherId);
        setAssignment(data);
        setGrades(data.grades || {});
        setStudentNotes(data.studentNotes || {});
        setSubmissionStatuses(data.submissionStatuses || {});
        setSaveStatus('saved');
      } catch (err: any) {
        setError(err.message || 'Failed to load assignment');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [assignmentId, teacherId]);

  // Compute live stats from current unsaved or saved grades
  const currentAssignmentWithGrades = useMemo(() => {
    if (!assignment) return null;
    return { ...assignment, grades };
  }, [assignment, grades]);

  const stats = useMemo(() => {
    return calculateGradingStats(currentAssignmentWithGrades);
  }, [currentAssignmentWithGrades]);

  // Handle cell grade change
  const setCellGrade = useCallback((studentId, mlsCode, value, explicitColor = null, isMissing = false, notAssessed = false) => {
    if (isMissing && assignment?.timeliness?.enabled) {
      const component = assignment.timeliness.scope === 'components' ? mlsCode : 'task';
      setSubmissionStatuses((previous) => ({
        ...previous, [studentId]: { ...previous[studentId], [component]: 'incomplete' },
      }));
    }
    setGrades((prev) => {
      const studentGrades = { ...(prev[studentId] || {}) };
      const existingCell = studentGrades[mlsCode] || {};

      let color = explicitColor;
      if (!explicitColor && value !== '' && value !== null && !isMissing && !notAssessed) {
        const num = Number(value);
        if (num >= 4) color = 'green';
        else if (num >= 3) color = 'lightgreen';
        else if (num >= 2) color = 'yellow';
        else color = 'red';
      }

      studentGrades[mlsCode] = {
        ...existingCell,
        score: value,
        color: isMissing || notAssessed ? null : color,
        isMissing,
        notAssessed,
        updatedAt: new Date().toISOString(),
      };

      return {
        ...prev,
        [studentId]: studentGrades,
      };
    });
    setSaveStatus('unsaved');
  }, [assignment]);

  // Handle overall student note change
  const handleStudentNoteChange = (studentId, note) => {
    setStudentNotes((prev) => ({
      ...prev,
      [studentId]: note,
    }));
    setSaveStatus('unsaved');
  };

  // Save changes
  const handleSave = async (explicitStatus = null) => {
    if (!assignment || syncStatus === 'syncing' || saveStatus === 'saving') return;
    try {
      setSaveStatus('saving');
      const updated = await updateAssignmentGrades(assignment.id, grades, studentNotes, explicitStatus, teacherId, submissionStatuses);
      setAssignment(updated);
      setSaveStatus('saved');

      // If finalizing & publishing grades, automatically sync to progression data points
      if (explicitStatus === 'graded') {
        setSyncStatus('syncing');
        setSyncMessage('Publishing grades and syncing to progression data points...');
        try {
          const syncRes = await publishAssignmentGradesToDataPoints(updated, grades, teacherId);
          if (syncRes.success) {
            setSyncStatus('synced');
            setSyncMessage(`✓ Published & synced ${syncRes.syncedCount} score(s) to Progression Grid`);
            setTimeout(() => setSyncMessage(''), 6000);
          } else {
            setSyncStatus('error');
            setSyncMessage(syncRes.error || 'Sync warning: some grades could not be synced to progression points.');
          }
        } catch (syncErr: any) {
          setSyncStatus('error');
          setSyncMessage(`Grades saved, but sync failed: ${syncErr.message}`);
        }
      }
    } catch (err: any) {
      alert(`Error saving grades: ${err.message}`);
      setSaveStatus('unsaved');
    }
  };

  // Explicit sync action for teacher
  const handleSyncToDataPoints = async () => {
    if (!assignment || syncStatus === 'syncing' || saveStatus === 'saving') return;
    try {
      setSyncStatus('syncing');
      setSyncMessage('Syncing grades to Student Progression Grid...');

      // Save any pending edits first
      const updated = await updateAssignmentGrades(assignment.id, grades, studentNotes, assignment.status, teacherId, submissionStatuses);
      setAssignment(updated);
      setSaveStatus('saved');

      const syncRes = await publishAssignmentGradesToDataPoints(updated, grades, teacherId);
      if (syncRes.success) {
        setSyncStatus('synced');
        setSyncMessage(`✓ Successfully synced ${syncRes.syncedCount} score(s) to Progression Grid`);
        setTimeout(() => setSyncMessage(''), 6000);
      } else {
        setSyncStatus('error');
        setSyncMessage(syncRes.error || 'Sync failed: please check network or student records');
      }
    } catch (err: any) {
      setSyncStatus('error');
      setSyncMessage(`Sync failed: ${err.message}`);
    }
  };

  // Keyboard navigation across cells
  const handleKeyDown = (e, studentIndex, standardIndex) => {
    const students = filteredStudents;
    const standards = assignment?.standards || [];

    if (e.key === 'ArrowRight' || e.key === 'Tab') {
      if (!e.shiftKey && standardIndex < standards.length - 1) {
        e.preventDefault();
        const nextStd = standards[standardIndex + 1];
        setActiveCell({ studentId: students[studentIndex].studentId, mlsCode: nextStd.mlsCode });
      }
    } else if (e.key === 'ArrowLeft' || (e.key === 'Tab' && e.shiftKey)) {
      if (standardIndex > 0) {
        e.preventDefault();
        const prevStd = standards[standardIndex - 1];
        setActiveCell({ studentId: students[studentIndex].studentId, mlsCode: prevStd.mlsCode });
      }
    } else if (e.key === 'ArrowDown' || e.key === 'Enter') {
      if (studentIndex < students.length - 1) {
        e.preventDefault();
        const nextStu = students[studentIndex + 1];
        setActiveCell({ studentId: nextStu.studentId, mlsCode: standards[standardIndex].mlsCode });
      }
    } else if (e.key === 'ArrowUp') {
      if (studentIndex > 0) {
        e.preventDefault();
        const prevStu = students[studentIndex - 1];
        setActiveCell({ studentId: prevStu.studentId, mlsCode: standards[standardIndex].mlsCode });
      }
    }
  };

  // Filter students based on name and grading status
  const filteredStudents = useMemo(() => {
    if (!assignment) return [];
    let list = assignment.students || [];

    if (studentFilter.trim()) {
      const q = studentFilter.toLowerCase().trim();
      list = list.filter((s) => (s.name || '').toLowerCase().includes(q));
    }

    if (completionFilter === 'ungraded') {
      const standards = assignment.standards || [];
      list = list.filter((s) => {
        const stuGrades = grades[s.studentId] || {};
        return standards.some((std) => !stuGrades[std.mlsCode] || stuGrades[std.mlsCode].score === '');
      });
    } else if (completionFilter === 'needs_support') {
      const standards = assignment.standards || [];
      list = list.filter((s) => {
        const stuGrades = grades[s.studentId] || {};
        return standards.some((std) => {
          const g = stuGrades[std.mlsCode];
          return g && (g.score === 1 || g.score === 2 || g.color === 'red' || g.color === 'yellow');
        });
      });
    }

    return list;
  }, [assignment, grades, studentFilter, completionFilter]);

  const predominantLevel = useMemo(() => {
    const dist = stats.distribution || {};
    const entries = [
      { label: 'Proficient (Level 4)', count: dist.proficient || 0, color: '#10b981' },
      { label: 'On Track (Level 3)', count: dist.onTrack || 0, color: '#22c55e' },
      { label: 'Learning Zone (Level 2)', count: dist.learningZone || 0, color: '#eab308' },
      { label: 'Needs Support (Level 1)', count: dist.notYet || 0, color: '#ef4444' },
    ];
    const sorted = [...entries].sort((a, b) => b.count - a.count);
    return sorted[0]?.count > 0 ? sorted[0] : { label: 'Ungraded', count: 0, color: '#64748b' };
  }, [stats.distribution]);

  const averageOverallScore = useMemo(() => {
    let sum = 0;
    let count = 0;
    Object.values(grades).forEach((stuObj) => {
      Object.values(stuObj).forEach((cell) => {
        const num = Number(cell?.score);
        if (Number.isFinite(num) && cell.score !== '' && cell.score !== 'M' && cell.score !== 'NA') {
          sum += num;
          count += 1;
        }
      });
    });
    return count > 0 ? (sum / count).toFixed(1) : '—';
  }, [grades]);

  const gradedStudentsCount = useMemo(() => {
    if (!assignment) return 0;
    const stds = assignment.standards || [];
    return (assignment.students || []).filter((s) => {
      const stuGrades = grades[s.studentId] || {};
      return stds.some((std) => {
        const cell = stuGrades[std.mlsCode];
        return cell && cell.score !== '' && cell.score !== null && cell.score !== undefined;
      });
    }).length;
  }, [assignment, grades]);

  const getStudentMetrics = useCallback((studentId) => {
    const stds = assignment?.standards || [];
    const stuGrades = grades[studentId] || {};
    let totalNumeric = 0;
    let sumNumeric = 0;
    let hasMissing = false;
    let hasNeedsSupport = false;

    stds.forEach((std) => {
      const cell = stuGrades[std.mlsCode];
      if (!cell) return;
      if (cell.isMissing || cell.score === 'M') hasMissing = true;
      const num = Number(cell.score);
      if (Number.isFinite(num) && cell.score !== '' && cell.score !== 'M' && cell.score !== 'NA') {
        sumNumeric += num;
        totalNumeric += 1;
        if (num <= 2 || cell.color === 'yellow' || cell.color === 'red') {
          hasNeedsSupport = true;
        }
      }
    });

    let overallLevel = 'Ungraded';
    let levelColor = '#94a3b8';
    let avg = null;

    if (totalNumeric > 0) {
      avg = (sumNumeric / totalNumeric).toFixed(1);
      if (avg >= 3.5) {
        overallLevel = 'Proficient';
        levelColor = '#10b981';
      } else if (avg >= 2.5) {
        overallLevel = 'On Track';
        levelColor = '#22c55e';
      } else if (avg >= 1.5) {
        overallLevel = 'Learning Zone';
        levelColor = '#eab308';
      } else {
        overallLevel = 'Needs Support';
        levelColor = '#ef4444';
      }
    }

    let submissionStatus = 'Assigned';
    if (totalNumeric === stds.length && stds.length > 0) {
      submissionStatus = 'Graded';
    } else if (totalNumeric > 0) {
      submissionStatus = 'In Progress';
    } else if (hasMissing) {
      submissionStatus = 'Missing';
    }

    return {
      average: avg,
      overallLevel,
      levelColor,
      hasNeedsSupport,
      submissionStatus,
    };
  }, [assignment, grades]);

  if (loading) {
    return (
      <div className="lms-assignments-container" style={{ textAlign: 'center', padding: '60px 20px' }}>
        <p>Loading spreadsheet grading grid...</p>
      </div>
    );
  }

  if (error || !assignment) {
    return (
      <div className="lms-assignments-container" style={{ textAlign: 'center', padding: '60px 20px' }}>
        <h2 style={{ color: '#dc2626' }}>Error Loading Assignment</h2>
        <p>{error || 'Assignment not found.'}</p>
        <button
          type="button"
          className="lms-btn-primary"
          onClick={() => navigate('/teacher/assignments')}
        >
          Return to Assignments Hub
        </button>
      </div>
    );
  }

  const standards = assignment.standards || [];

  return (
    <div className="lms-assignments-container" style={{ maxWidth: '100%', padding: '16px 24px' }}>
      {/* ── TOP BANNER ────────────────────────────────────────────────────── */}
      <div className="lms-grading-shell">
        <div className="lms-grading-banner">
          <div className="lms-banner-left">
            <button
              type="button"
              className="lms-banner-back"
              onClick={() => navigate('/teacher/assignments')}
            >
              ← Back to Assignments Hub
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h1 className="lms-banner-title">{assignment.title}</h1>
              <span className="lms-badge lms-badge-type">{assignment.type}</span>
              <span className={`lms-badge lms-badge-${assignment.status}`}>
                {assignment.status}
              </span>
            </div>
            <div className="lms-banner-meta">
              <span><strong>Class:</strong> {assignment.className}</span>
              <span><strong>Subject:</strong> {assignment.subject}</span>
              <span><strong>Due:</strong> {assignment.dueDate || '—'}</span>
              <span><strong>Students:</strong> {assignment.students?.length || 0}</span>
              <span><strong>Standards Assessed:</strong> {standards.length}</span>
            </div>
          </div>

          <div className="lms-banner-actions">
            <span
              style={{
                fontSize: '0.82rem',
                fontWeight: 600,
                color: saveStatus === 'saved' ? '#86efac' : '#fef08a',
                marginRight: '8px',
              }}
            >
              {saveStatus === 'saved'
                ? '✓ All changes saved'
                : saveStatus === 'saving'
                ? 'Saving grades...'
                : '● Unsaved changes'}
            </span>

            {assignment.status !== 'draft' && (
              <button
                type="button"
                className="lms-btn-secondary lms-btn-sm"
                onClick={() => navigate(`/teacher/data-points-report?assignment=${encodeURIComponent(assignment.id)}`)}
                title="See this assignment's published data points in View Data"
              >
                📈 View Data
              </button>
            )}

            <button
              type="button"
              className="lms-btn-secondary lms-btn-sm"
              onClick={() => navigate(`/teacher/assignments/${assignment.id}/print`)}
            >
              🖨️ Print Grid
            </button>

            <button
              type="button"
              className="lms-btn-secondary lms-btn-sm"
              onClick={handleSyncToDataPoints}
              disabled={syncStatus === 'syncing' || saveStatus === 'saving'}
              title="Sync graded scores directly into Academic Eye Student Progression Grid"
            >
              {syncStatus === 'syncing' ? 'Syncing...' : '🔄 Sync to Progression Grid'}
            </button>

            <button
              type="button"
              className="lms-btn-secondary lms-btn-sm"
              onClick={() => handleSave(assignment.status === 'assigned' ? 'in_progress' : assignment.status)}
              disabled={saveStatus === 'saving' || syncStatus === 'syncing'}
            >
              Save Draft
            </button>

            <button
              type="button"
              className="lms-btn-primary lms-btn-sm"
              style={{ background: '#10b981', borderColor: '#059669' }}
              onClick={() => handleSave('graded')}
              disabled={saveStatus === 'saving' || syncStatus === 'syncing'}
              title="Finalize grades and automatically sync to Progression Grid"
            >
              ✓ Finalize & Publish Grades
            </button>
          </div>
        </div>

        {/* Sync / feedback message banner */}
        {syncMessage && (
          <div
            style={{
              padding: '8px 16px',
              backgroundColor: syncStatus === 'error' ? '#fef2f2' : '#ecfdf5',
              borderBottom: `1px solid ${syncStatus === 'error' ? '#fca5a5' : '#a7f3d0'}`,
              color: syncStatus === 'error' ? '#991b1b' : '#065f46',
              fontSize: '0.85rem',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span>{syncMessage}</span>
            <button
              type="button"
              onClick={() => setSyncMessage('')}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                fontSize: '1rem',
                padding: '0 4px',
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* ── VIEW TABS & ACTIONS BAR (Section 7 Requirement) ───────────── */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 16px',
            backgroundColor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            flexWrap: 'wrap',
            gap: '10px',
          }}
        >
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              className={`th-tab-btn ${activeTab === 'grid' ? 'active' : ''}`}
              onClick={() => setActiveTab('grid')}
            >
              📊 Interactive Grading Grid
            </button>
            <button
              type="button"
              className={`th-tab-btn ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              📋 Assignment Details & Student Performance
            </button>
            <button
              type="button"
              className={`th-tab-btn ${activeTab === 'replies' ? 'active' : ''}`}
              onClick={() => setActiveTab('replies')}
            >
              💬 Student Replies
            </button>
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              type="button"
              className="lms-btn-secondary lms-btn-sm"
              onClick={handleOpenEditAssignment}
              disabled={saveStatus === 'saving'}
            >
              ✏️ Edit Assignment
            </button>
            <button
              type="button"
              className="lms-btn-secondary lms-btn-sm"
              onClick={handleDuplicate}
            >
              📄 Duplicate
            </button>
            <button
              type="button"
              className="lms-btn-secondary lms-btn-sm"
              onClick={handleExportCSV}
            >
              📥 Export Scores (CSV)
            </button>
          </div>
        </div>

        {activeTab === 'grid' ? (
          <>
            {/* ── SUMMARY STATS BAR ────────────────────────────────────────────── */}
            <div className="lms-grading-summary-bar">
          <div className="lms-mastery-stats-row">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '16px' }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#334155' }}>Completion:</span>
              <div style={{ width: '120px' }} className="lms-progress-track">
                <div
                  className="lms-progress-fill"
                  style={{
                    width: `${stats.percentComplete}%`,
                    backgroundColor: stats.percentComplete === 100 ? '#10b981' : '#2563eb',
                  }}
                />
              </div>
              <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#1e293b' }}>
                {stats.percentComplete}% ({stats.gradedCells}/{stats.totalCells})
              </span>
            </div>

            {/* Performance breakdown pills */}
            <span className="lms-stat-pill proficient" title="Proficient (Level 4 / Green)">
              Proficient: {stats.distribution.proficient}
            </span>
            <span className="lms-stat-pill onTrack" title="On Track (Level 3 / Light Green)">
              On Track: {stats.distribution.onTrack}
            </span>
            <span className="lms-stat-pill learningZone" title="Learning Zone (Level 2 / Yellow)">
              Learning Zone: {stats.distribution.learningZone}
            </span>
            <span className="lms-stat-pill notYet" title="Not Yet (Level 1 / Red)">
              Not Yet: {stats.distribution.notYet}
            </span>
            {stats.missingCells > 0 && (
              <span className="lms-stat-pill" style={{ background: '#ffedd5', color: '#c2410c' }}>
                Missing: {stats.missingCells}
              </span>
            )}
          </div>

          {/* Quick Filters */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="text"
              className="lms-search-input"
              style={{ padding: '6px 12px 6px 32px', width: '180px', fontSize: '0.8rem' }}
              placeholder="Filter student..."
              value={studentFilter}
              onChange={(e) => setStudentFilter(e.target.value)}
            />

            <select
              className="lms-select"
              style={{ padding: '6px 10px', fontSize: '0.8rem' }}
              value={completionFilter}
              onChange={(e) => setCompletionFilter(e.target.value)}
            >
              <option value="all">All Students ({assignment.students?.length || 0})</option>
              <option value="ungraded">Needs Grading</option>
              <option value="needs_support">Needs Support (Levels 1-2)</option>
            </select>
          </div>
        </div>

        {/* ── SPREADSHEET GRADING GRID ─────────────────────────────────────── */}
        <div className="lms-grid-scroll-container">
          <table className="lms-grid-table" role="grid" aria-label="Student-by-standard grading grid">
            <thead>
              <tr>
                {/* Sticky Student Column Header */}
                <th scope="col" style={{ zIndex: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Student Name</span>
                    <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                      {filteredStudents.length} Students
                    </span>
                  </div>
                </th>

                {/* Assessed Standard Column Headers */}
                {standards.map((std, stdIdx) => {
                  const sStat = stats.standardStats?.[std.mlsCode];
                  return (
                    <th key={std.mlsCode} scope="col">
                      <div style={{ fontSize: '0.68rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>
                        {std.domain || 'Standard'}
                      </div>
                      <div className="lms-th-mls" title={`${std.domain ? `${std.domain} › ` : ''}${std.strand ? `${std.strand} › ` : ''}${std.mlsCode}`}>
                        {std.mlsCode}
                      </div>
                      <div className="lms-th-title" title={std.description}>
                        {std.friendlyTitle || std.description}
                      </div>
                      <div className="lms-th-average" title="Average standard score across assessed students">
                        {sStat?.average ? `Avg: ${sStat.average}` : 'Not Graded'}
                      </div>
                    </th>
                  );
                })}

                {timingComponents.map((component) => (
                  <th key={`timing-${component.key}`} scope="col">
                    Submission: {component.label}
                    <div className="lms-th-title">{timelinessStandard(assignment.timeliness.grade || assignment.grade)}</div>
                  </th>
                ))}
                {/* Overall Student Notes Column */}
                <th scope="col" style={{ width: '220px', textAlign: 'left' }}>
                  Student Notes / Observations
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredStudents.map((stu, stuIdx) => {
                const stuGrades = grades[stu.studentId] || {};
                const stuNote = studentNotes[stu.studentId] || '';
                const initials = (stu.name || 'S')
                  .split(' ')
                  .map((p) => p[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase();

                return (
                  <tr key={stu.studentId}>
                    {/* Sticky Student Cell */}
                    <td>
                      <div className="lms-student-cell">
                        <div className="lms-student-avatar">{initials}</div>
                        <div>
                          <div className="lms-student-name">
                            <button
                              type="button"
                              className="lms-student-profile-link"
                              onClick={() => navigate(`/teacher/students/${stu.studentId}`)}
                              title={`View Student Profile for ${stu.name}`}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                padding: 0,
                                font: 'inherit',
                                color: 'inherit',
                                fontWeight: 700,
                                textAlign: 'left',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textDecorationColor: 'transparent',
                                transition: 'text-decoration-color 0.15s ease',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.textDecorationColor = '#2563eb'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.textDecorationColor = 'transparent'; }}
                            >
                              {stu.name}
                            </button>
                          </div>
                          <div className="lms-student-id">{stu.studentNumber}</div>
                        </div>
                      </div>
                    </td>

                    {/* Standard Score Cells */}
                    {standards.map((std, stdIdx) => {
                      const cell = stuGrades[std.mlsCode] || { score: '', notes: '' };
                      const isActive =
                        activeCell?.studentId === stu.studentId && activeCell?.mlsCode === std.mlsCode;
                      const hasNotes = Boolean(cell.notes && cell.notes.trim());

                      return (
                        <td
                          key={std.mlsCode}
                          tabIndex={0}
                          onFocus={() => setActiveCell({ studentId: stu.studentId, mlsCode: std.mlsCode })}
                          onKeyDown={(e) => handleKeyDown(e, stuIdx, stdIdx)}
                          style={{
                            background: isActive ? '#eff6ff' : '#ffffff',
                            outline: isActive ? '2px solid #2563eb' : 'none',
                          }}
                        >
                          <div className="lms-cell-box">
                            <div className={`lms-score-btn-group${assignment.scoringMethod === SCORING_METHODS.MASTERY ? ' lms-mastery-color-group' : ''}`}
                              role="group" aria-label={`${stu.name} ${std.mlsCode} score choices`}>
                              {MASTERY_CHOICES.map((choice) => {
                                const colorMode = assignment.scoringMethod === SCORING_METHODS.MASTERY;
                                const selected = !cell.isMissing && !cell.notAssessed
                                  && String(cell.score) === String(choice.score);
                                return (
                                  <button key={choice.score}
                                    type="button"
                                    className={`lms-score-btn score-${choice.score}${colorMode ? ' lms-mastery-color-btn' : ''}${selected ? ' selected' : ''}`}
                                    onClick={() => setCellGrade(stu.studentId, std.mlsCode, choice.score, choice.color)}
                                    aria-label={colorMode ? `${choice.colorLabel}: ${choice.label}` : undefined}
                                    aria-pressed={selected}
                                    title={`${stu.name} / ${std.mlsCode}: ${choice.label}`}
                                  >
                                    {colorMode ? null : choice.score}
                                  </button>
                                );
                              })}
                            </div>

                            {/* Missing / NA and Cell Note Actions */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <button
                                type="button"
                                style={{
                                  background: cell.isMissing ? '#ea580c' : 'transparent',
                                  color: cell.isMissing ? '#ffffff' : '#94a3b8',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: '4px',
                                  fontSize: '0.68rem',
                                  padding: '1px 4px',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                }}
                                onClick={() =>
                                  setCellGrade(
                                    stu.studentId,
                                    std.mlsCode,
                                    cell.isMissing ? '' : 'M',
                                    null,
                                    !cell.isMissing,
                                    false
                                  )
                                }
                                title="Mark Missing (M)"
                              >
                                M
                              </button>

                              <button
                                type="button"
                                style={{
                                  background: cell.notAssessed ? '#64748b' : 'transparent',
                                  color: cell.notAssessed ? '#ffffff' : '#94a3b8',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: '4px',
                                  fontSize: '0.68rem',
                                  padding: '1px 4px',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                }}
                                onClick={() =>
                                  setCellGrade(
                                    stu.studentId,
                                    std.mlsCode,
                                    cell.notAssessed ? '' : 'NA',
                                    null,
                                    false,
                                    !cell.notAssessed
                                  )
                                }
                                title="Mark Not Assessed (NA)"
                              >
                                NA
                              </button>

                              <button
                                type="button"
                                className={`lms-cell-note-btn ${hasNotes ? 'has-notes' : ''}`}
                                onClick={() =>
                                  setNoteModal({
                                    studentId: stu.studentId,
                                    studentName: stu.name,
                                    mlsCode: std.mlsCode,
                                    note: cell.notes || '',
                                  })
                                }
                                title={hasNotes ? `Note: ${cell.notes}` : 'Add comment/observation'}
                              >
                                💬
                              </button>
                            </div>
                          </div>
                        </td>
                      );
                    })}

                    {timingComponents.map((component) => (
                      <td key={`timing-${component.key}`}>
                        <select className="lms-select"
                          aria-label={`${stu.name} submission: ${component.label}`}
                          disabled={syncStatus === 'syncing' || saveStatus === 'saving'}
                          value={submissionStatusFor({ ...assignment, submissionStatuses }, stu.studentId, component.key).id}
                          onChange={(e) => changeSubmissionStatus(stu.studentId, component.key, e.target.value)}>
                          {SUBMISSION_STATUSES.map((status) => (
                            <option key={status.id} value={status.id}>{status.label}</option>
                          ))}
                        </select>
                      </td>
                    ))}
                    {/* Overall Notes for this Student */}
                    <td>
                      <input
                        type="text"
                        className="lms-input"
                        style={{ padding: '6px 10px', fontSize: '0.8rem' }}
                        placeholder="Add student note..."
                        value={stuNote}
                        onChange={(e) => handleStudentNoteChange(stu.studentId, e.target.value)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
          </>
        ) : activeTab === 'replies' ? (
          <TaskThreadsPanel assignment={assignment} canReply={assignment.canEdit !== false} />
        ) : (
          /* ── SECTION 7: ASSIGNMENT DETAILS & STUDENT PERFORMANCE VIEW ── */
          <div style={{ padding: '24px', background: '#f8fafc' }}>
            {/* Top Cards Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginBottom: '24px' }}>
              {/* Assignment Summary Card */}
              <div style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#1e3a8a', fontWeight: 800 }}>
                    Assignment Overview
                  </h3>
                  <span className={`lms-badge lms-badge-${assignment.status}`}>
                    {assignment.status}
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', fontSize: '0.82rem', marginBottom: '14px' }}>
                  <div>
                    <span style={{ color: '#64748b', display: 'block', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' }}>Subject</span>
                    <strong style={{ color: '#0f172a' }}>{assignment.subject}</strong>
                  </div>
                  <div>
                    <span style={{ color: '#64748b', display: 'block', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' }}>Type</span>
                    <strong style={{ color: '#0f172a' }}>{assignment.type}</strong>
                  </div>
                  <div>
                    <span style={{ color: '#64748b', display: 'block', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' }}>Assigned Date</span>
                    <strong style={{ color: '#0f172a' }}>{assignment.assignedDate || '—'}</strong>
                  </div>
                  <div>
                    <span style={{ color: '#64748b', display: 'block', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' }}>Due Date</span>
                    <strong style={{ color: '#0f172a' }}>{assignment.dueDate || '—'}</strong>
                  </div>
                  {assignment.assessmentDate && (
                    <div>
                      <span style={{ color: '#64748b', display: 'block', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' }}>Assessment Window</span>
                      <strong style={{ color: '#0f172a' }}>{assignment.assessmentDate}</strong>
                    </div>
                  )}
                  <div>
                    <span style={{ color: '#64748b', display: 'block', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' }}>Target Class</span>
                    <strong style={{ color: '#0f172a' }}>{assignment.className}</strong>
                  </div>
                </div>

                {assignment.instructions && (
                  <div style={{ marginBottom: '14px' }}>
                    <span style={{ color: '#64748b', display: 'block', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>
                      Instructions & Learning Goals
                    </span>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#334155', lineHeight: 1.5, background: '#f8fafc', padding: '10px 12px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                      {assignment.instructions}
                    </p>
                  </div>
                )}

                {assignment.teacherNotes && (
                  <div style={{ marginBottom: '14px' }}>
                    <span style={{ color: '#64748b', display: 'block', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>
                      Instructional Reflections & Rubric Notes
                    </span>
                    <p style={{ margin: 0, fontSize: '0.82rem', color: '#475569', fontStyle: 'italic', background: '#fffbeb', padding: '8px 12px', borderRadius: '8px', border: '1px solid #fef3c7' }}>
                      💡 {assignment.teacherNotes}
                    </p>
                  </div>
                )}

                <div>
                  <span style={{ color: '#64748b', display: 'block', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '6px' }}>
                    Attached Resources & Materials
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    <span style={{ fontSize: '0.75rem', background: '#f1f5f9', color: '#475569', padding: '3px 8px', borderRadius: '6px' }}>
                      📄 4-Level Standards Rubric
                    </span>
                    <span style={{ fontSize: '0.75rem', background: '#f1f5f9', color: '#475569', padding: '3px 8px', borderRadius: '6px' }}>
                      📊 Academic Eye Scale Guide
                    </span>
                    <span style={{ fontSize: '0.75rem', background: '#f1f5f9', color: '#475569', padding: '3px 8px', borderRadius: '6px' }}>
                      🎯 Priority Standard Alignment
                    </span>
                  </div>
                </div>
              </div>

              {/* Student Performance Summary Card */}
              <div style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <h3 style={{ margin: '0 0 14px', fontSize: '1.05rem', color: '#1e3a8a', fontWeight: 800 }}>
                  Class Performance Summary
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '18px' }}>
                  <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', fontWeight: 700, color: '#64748b' }}>Students Assigned</span>
                    <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a' }}>{assignment.students?.length || 0}</div>
                  </div>
                  <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', fontWeight: 700, color: '#64748b' }}>Evaluated Students</span>
                    <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#2563eb' }}>
                      {gradedStudentsCount} <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>({assignment.students?.length ? Math.round((gradedStudentsCount / assignment.students.length) * 100) : 0}%)</span>
                    </div>
                  </div>
                  <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', fontWeight: 700, color: '#64748b' }}>Average Score</span>
                    <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#059669' }}>
                      {averageOverallScore} <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#64748b' }}>/ 4.0</span>
                    </div>
                  </div>
                  <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', fontWeight: 700, color: '#64748b' }}>Predominant Level</span>
                    <div style={{ fontSize: '1.05rem', fontWeight: 800, color: predominantLevel.color, marginTop: '4px' }}>
                      {predominantLevel.label}
                    </div>
                  </div>
                </div>

                <div>
                  <span style={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b', display: 'block', marginBottom: '8px' }}>
                    Distribution Across Performance Levels
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: '2px' }}>
                        <span style={{ color: '#059669', fontWeight: 700 }}>Level 4 · Proficient / Advanced</span>
                        <strong style={{ color: '#0f172a' }}>{stats.distribution.proficient}</strong>
                      </div>
                      <div style={{ height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: '#10b981', width: `${stats.totalCells > 0 ? (stats.distribution.proficient / stats.totalCells) * 100 : 0}%` }} />
                      </div>
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: '2px' }}>
                        <span style={{ color: '#16a34a', fontWeight: 700 }}>Level 3 · On Track</span>
                        <strong style={{ color: '#0f172a' }}>{stats.distribution.onTrack}</strong>
                      </div>
                      <div style={{ height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: '#22c55e', width: `${stats.totalCells > 0 ? (stats.distribution.onTrack / stats.totalCells) * 100 : 0}%` }} />
                      </div>
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: '2px' }}>
                        <span style={{ color: '#ca8a04', fontWeight: 700 }}>Level 2 · Learning Zone / Approaching</span>
                        <strong style={{ color: '#0f172a' }}>{stats.distribution.learningZone}</strong>
                      </div>
                      <div style={{ height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: '#eab308', width: `${stats.totalCells > 0 ? (stats.distribution.learningZone / stats.totalCells) * 100 : 0}%` }} />
                      </div>
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: '2px' }}>
                        <span style={{ color: '#dc2626', fontWeight: 700 }}>Level 1 · Needs Support / Not Yet</span>
                        <strong style={{ color: '#0f172a' }}>{stats.distribution.notYet}</strong>
                      </div>
                      <div style={{ height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', background: '#ef4444', width: `${stats.totalCells > 0 ? (stats.distribution.notYet / stats.totalCells) * 100 : 0}%` }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Target Standards Breakdown */}
            <div style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#1e3a8a', fontWeight: 800 }}>
                  Target Standards Breakdown ({standards.length})
                </h3>
                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                  Priority Learning Standards
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '14px' }}>
                {standards.map((std) => {
                  const sStat = stats.standardStats?.[std.mlsCode] || {};
                  return (
                    <div
                      key={std.mlsCode}
                      style={{
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '10px',
                        padding: '14px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                          <span className="lms-cell-code" style={{ fontSize: '0.82rem' }}>{std.mlsCode}</span>
                          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: sStat.average >= 3 ? '#059669' : '#d97706' }}>
                            Avg: {sStat.average ? `${sStat.average} / 4.0` : 'Not Graded'}
                          </span>
                        </div>
                        <h4 style={{ margin: '0 0 4px', fontSize: '0.92rem', color: '#0f172a' }}>
                          {std.friendlyTitle || std.mlsCode}
                        </h4>
                        <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '8px' }}>
                          {std.domain} {std.strand ? `• ${std.strand}` : ''}
                        </div>
                        <p style={{ margin: 0, fontSize: '0.78rem', color: '#334155', lineHeight: 1.4 }}>
                          {std.description}
                        </p>
                      </div>

                      <div style={{ marginTop: '12px', paddingTop: '8px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#64748b' }}>
                        <span>Graded: {sStat.gradedCount || 0}/{assignment.students?.length || 0}</span>
                        <span>Proficient Rate: {sStat.totalCount > 0 ? Math.round(((sStat.proficient + sStat.onTrack) / sStat.totalCount) * 100) : 0}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Student List & Performance Table */}
            <div style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#1e3a8a', fontWeight: 800 }}>
                    Student Roster & Performance
                  </h3>
                  <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                    Individual standards scores, support indicators, submission status, and direct profile links.
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    className="lms-search-input"
                    placeholder="Search student..."
                    value={studentFilter}
                    onChange={(e) => setStudentFilter(e.target.value)}
                    style={{ width: '220px', fontSize: '0.82rem' }}
                  />
                  <button
                    type="button"
                    className="th-btn-primary th-btn-sm"
                    onClick={() => setActiveTab('grid')}
                  >
                    Open Grading Grid →
                  </button>
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table className="lms-grid-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', minWidth: '160px' }}>Student Name</th>
                      <th style={{ minWidth: '110px' }}>Overall Level</th>
                      <th style={{ minWidth: '120px' }}>Support Status</th>
                      {standards.map((std) => (
                        <th key={std.mlsCode} style={{ minWidth: '90px' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>{std.mlsCode}</span>
                        </th>
                      ))}
                      {timingComponents.map((component) => <th key={`timing-${component.key}`}>Timing: {component.label}</th>)}
                      <th style={{ minWidth: '110px' }}>Submission</th>
                      <th style={{ minWidth: '160px', textAlign: 'left' }}>Teacher Feedback</th>
                      <th style={{ minWidth: '120px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((stu) => {
                      const metrics = getStudentMetrics(stu.studentId);
                      const stuGrades = grades[stu.studentId] || {};
                      const note = studentNotes[stu.studentId] || '';

                      return (
                        <tr key={stu.studentId}>
                          <td style={{ textAlign: 'left' }}>
                            <strong style={{ color: '#0f172a', fontSize: '0.88rem' }}>{stu.name}</strong>
                            <div style={{ fontSize: '0.72rem', color: '#64748b' }}>ID: {stu.studentId}</div>
                          </td>

                          <td>
                            <span
                              style={{
                                backgroundColor: `${metrics.levelColor}15`,
                                color: metrics.levelColor,
                                border: `1px solid ${metrics.levelColor}`,
                                borderRadius: '6px',
                                padding: '2px 8px',
                                fontSize: '0.75rem',
                                fontWeight: 700,
                              }}
                            >
                              {metrics.overallLevel} {metrics.average ? `(${metrics.average})` : ''}
                            </span>
                          </td>

                          <td>
                            {metrics.hasNeedsSupport ? (
                              <span
                                style={{
                                  backgroundColor: '#fef2f2',
                                  color: '#dc2626',
                                  borderRadius: '6px',
                                  padding: '2px 8px',
                                  fontSize: '0.72rem',
                                  fontWeight: 700,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                              >
                                ⚠️ Needs Support
                              </span>
                            ) : metrics.overallLevel === 'Ungraded' ? (
                              <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>—</span>
                            ) : (
                              <span
                                style={{
                                  backgroundColor: '#ecfdf5',
                                  color: '#059669',
                                  borderRadius: '6px',
                                  padding: '2px 8px',
                                  fontSize: '0.72rem',
                                  fontWeight: 700,
                                }}
                              >
                                ✓ On Target
                              </span>
                            )}
                          </td>

                          {standards.map((std) => {
                            const cell = stuGrades[std.mlsCode];
                            const isMissing = cell?.isMissing || cell?.score === 'M';
                            const isNA = cell?.notAssessed || cell?.score === 'NA';
                            const score = cell?.score;

                            return (
                              <td key={std.mlsCode} style={{ textAlign: 'center' }}>
                                {isMissing ? (
                                  <span style={{ color: '#ea580c', fontWeight: 700, fontSize: '0.75rem' }}>M</span>
                                ) : isNA ? (
                                  <span style={{ color: '#64748b', fontSize: '0.75rem' }}>NA</span>
                                ) : score !== '' && score !== null && score !== undefined ? (
                                  <span
                                    className={`lms-cell-badge ${
                                      cell.color || (score >= 4 ? 'green' : score >= 3 ? 'lightgreen' : score >= 2 ? 'yellow' : 'red')
                                    }`}
                                    style={{ display: 'inline-block', width: '28px', height: '28px', lineHeight: '28px' }}
                                  >
                                    {score}
                                  </span>
                                ) : (
                                  <span style={{ color: '#cbd5e1' }}>—</span>
                                )}
                              </td>
                            );
                          })}

                          {timingComponents.map((component) => (
                            <td key={`timing-${component.key}`}>
                              {submissionStatusFor({ ...assignment, submissionStatuses }, stu.studentId, component.key).label}
                            </td>
                          ))}
                          <td>
                            <span
                              className="lms-badge"
                              style={{
                                backgroundColor: metrics.submissionStatus === 'Graded' ? '#dcfce7' : metrics.submissionStatus === 'In Progress' ? '#fef9c3' : '#f1f5f9',
                                color: metrics.submissionStatus === 'Graded' ? '#166534' : metrics.submissionStatus === 'In Progress' ? '#854d0e' : '#475569',
                                fontSize: '0.72rem',
                              }}
                            >
                              {metrics.submissionStatus}
                            </span>
                          </td>

                          <td style={{ textAlign: 'left', fontSize: '0.8rem', color: '#475569' }}>
                            {note || <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>No notes</span>}
                          </td>

                          <td>
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <button
                                type="button"
                                className="th-btn-secondary th-btn-sm"
                                style={{ padding: '2px 6px', fontSize: '0.72rem' }}
                                onClick={() => navigate(`/teacher/student-progress/${stu.studentId}/${assignment.classId}`)}
                                title="View Student Progress Grid"
                              >
                                Progress
                              </button>
                              <button
                                type="button"
                                className="th-btn-secondary th-btn-sm"
                                style={{ padding: '2px 6px', fontSize: '0.72rem' }}
                                onClick={() => navigate(`/teacher/student/${stu.studentId}`)}
                                title="Open Student Profile"
                              >
                                Profile
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>


      {/* ── CELL NOTE MODAL / POPOVER ───────────────────────────────────────── */}
      {noteModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
          onClick={() => setNoteModal(null)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              padding: '24px',
              width: '90%',
              maxWidth: '450px',
              boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 6px', color: '#1e3a8a', fontSize: '1.1rem' }}>
              Standard Observation & Note
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: '#64748b' }}>
              Student: <strong>{noteModal.studentName}</strong> | Standard: <strong>{noteModal.mlsCode}</strong>
            </p>

            <textarea
              className="lms-textarea"
              rows={4}
              placeholder="e.g. Student identified evidence in line 12 but struggled to explain connotation."
              value={noteModal.note}
              onChange={(e) => setNoteModal({ ...noteModal, note: e.target.value })}
              autoFocus
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
              <button
                type="button"
                className="lms-btn-secondary"
                onClick={() => setNoteModal(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="lms-btn-primary"
                onClick={() => {
                  setGrades((prev) => {
                    const stuGrades = { ...(prev[noteModal.studentId] || {}) };
                    const cell = { ...(stuGrades[noteModal.mlsCode] || {}) };
                    cell.notes = noteModal.note;
                    stuGrades[noteModal.mlsCode] = cell;
                    return { ...prev, [noteModal.studentId]: stuGrades };
                  });
                  setSaveStatus('unsaved');
                  setNoteModal(null);
                }}
              >
                Save Note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
